import React, { useEffect, useState } from "react";
import Loader from "../loader/Loader";
import "./posts.css";

export const Posts: React.FC = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [showDevOverlay, setShowDevOverlay] = useState(false);
  const [showCloudAnimation, setShowCloudAnimation] = useState(false);

  useEffect(() => {
    if (!sessionStorage.getItem("devOverlayShown")) {
      setShowDevOverlay(true);
    }
  }, []);

  const handleEnterHome = () => {
    setShowDevOverlay(false);
    setShowCloudAnimation(true);
    sessionStorage.setItem("devOverlayShown", "true");
    setTimeout(() => {
      setShowCloudAnimation(false);
    }, 2500); // cloud animation duration (2.5s)
  };

  const showcaseVideos = [
    {
      type: "video",
      url: "https://www.youtube.com/embed/z8VojcG10Go?si=Da_4AC1jSkjzsN3p",
      title: "Gameplay Demo #1",
    },
    {
      type: "video",
      url: "https://www.youtube.com/embed/dnUg4wAxrWs?si=iN7X7oG7H4rSDpov",
      title: "Gameplay Demo #2",
    },
    {
      type: "video",
      url: "https://www.youtube.com/embed/_1DKW4Cl_L4?si=cI7FJHrtlWA7N4Pi",
      title: "Gameplay Demo #3",
    },
    {
      type: "video",
      url: "https://www.youtube.com/embed/GP311vLMxTY?si=3VMcYYVul8N7cT8G",
      title: "Gameplay Demo #4",
    },
    {
      type: "image",
      url: "/SurvivalInteractive.png",
      title: "Fan Made",
    },
  ];

  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [selectedVideo, setSelectedVideo] = useState<string>(
    showcaseVideos[0].url
  );

  const useRef = <T,>(initialValue: T | null): { current: T | null } => {
    const [ref] = useState<{ current: T | null }>({ current: initialValue });
    return ref;
  };

  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const moveCarousel = (direction: number) => {
    // calculate new index
    const newIndex = Math.max(
      0,
      Math.min(currentIndex + direction, showcaseVideos.length - 1)
    );
    setCurrentIndex(newIndex);

    // update main video automatically
    setSelectedVideo(showcaseVideos[newIndex].url);

    // scroll the carousel visually
    if (wrapperRef.current) {
      const itemWidth =
        wrapperRef.current.querySelector<HTMLElement>(".carousel__item")
          ?.offsetWidth || 0;
      wrapperRef.current.style.transform = `translateX(-${
        newIndex * (itemWidth + 20) // 20 = gap
      }px)`;
    }
  };

  if (isLoading) return <Loader />;

  return (
    <>
      {showDevOverlay && (
        <div
          className="dev-overlay"
          style={{
            width: "100vw",
            height: "100vh",
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            zIndex: 10000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundImage: 'url("/ocean-map.webp")',
            backgroundSize: "cover",
            backgroundPosition: "center",
            backgroundRepeat: "no-repeat",
          }}
        >
          <div className="dev-overlay-content">
            <div className="dev-overlay-image-container">
              <img
                src="/cloud-main.avif"
                alt="cloud"
                className="dev-overlay-logo"
              />
              <div className="dev-overlay-text">
                <h1 style={{ textAlign: "center", color: "var(--color-primary)" }}>
                  Under development
                </h1>
                <button className="dev-overlay-btn" onClick={handleEnterHome}>
                  Enter Home
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showCloudAnimation && (
        <div className="cloud-animation-overlay">
          <div
            className="cloud-animation-bg"
            style={{
              width: "100vw",
              height: "100vh",
              borderRadius: 0,
              boxShadow: "0 0 80px 20px #222",
              backgroundImage: 'url("/cloud-animation.webp")',
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              animation: "foggyMoveOnce 10s ease-in-out",
            }}
          />
        </div>
      )}
      <div id="background__home">
        {/* Hero Section */}
        <section className="hero__box">
          <div className="subHero__box">
            <div className="hero__content">
              <h1>Survival Interactive</h1>
              <p>Survive the darkness. Build kingdoms. Conquer the world.</p>
              <a href="/download" className="download__button">
                Download Now
              </a>
            </div>
          </div>
        </section>

        {/* Dynamic Trailer Section */}
        <section className="video__section">
          <h2>Survival Interactive Showcase</h2>
          <div className="video__container">
            <iframe
              width="100%"
              height="500"
              src={selectedVideo}
              title="Survival Interactive Trailer"
              frameBorder="0"
              allow="autoplay; encrypted-media; fullscreen"
              allowFullScreen
            ></iframe>
          </div>
        </section>

        <hr />

        {/* Showcase Carousel */}
        <section className="showcase__section">
          <div className="carousel__container">
            <button
              className="carousel__arrow left"
              onClick={() => moveCarousel(-1)}
              disabled={currentIndex === 0}
            >
              &#10094;
            </button>

            {showcaseVideos.map((item, index) => (
              <div
                className={`carousel__item ${
                  selectedVideo === item.url ? "active" : ""
                }`}
                key={index}
                onClick={() => {
                  setSelectedVideo(item.url);
                  setCurrentIndex(index);

                  if (wrapperRef.current) {
                    const itemWidth =
                      wrapperRef.current.querySelector<HTMLElement>(
                        ".carousel__item"
                      )?.offsetWidth || 0;
                    wrapperRef.current.style.transform = `translateX(-${
                      index * (itemWidth + 20)
                    }px)`;
                  }
                }}
              >
                {item.type === "video" ? (
                  <iframe
                    src={item.url}
                    title={item.title}
                    frameBorder="0"
                    allow="autoplay; encrypted-media; fullscreen"
                    allowFullScreen
                  ></iframe>
                ) : (
                  <img
                    src={item.url}
                    alt={item.title}
                    style={{ width: "100%", borderRadius: "10px" }}
                  />
                )}
                <p>{item.title}</p>
              </div>
            ))}

            <button
              className="carousel__arrow right"
              onClick={() => moveCarousel(1)}
              disabled={currentIndex === showcaseVideos.length - 1}
            >
              &#10095;
            </button>
          </div>
        </section>
      </div>
    </>
  );
};
function useRef<T>(arg0: null) {
  throw new Error("Function not implemented.");
}

